import React, { useEffect, useState } from 'react';
import PageContentWrapper from 'src/components/PageContentWrapper/PageContentWrapper';
import { CreateProjectStepsTitle } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStepsTitle/CreateProjectStepsTitle';
import { Button } from 'src/components/ui/button';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector } from 'src/store';
import { ProductsType } from 'src/constants/products';
import { PATH_DASHBOARD } from 'src/constants/spa-routes';
import {
  useGetAllConfigurationSettingsQuery,
  useGetConfigurationSettingsPromptQuery,
  useUpdateConfigurationSettingsPromptMutation,
} from 'src/services';
import moment from 'moment';
import { useLoader } from 'src/provider/LoaderProvider';
import { useDispatch } from 'react-redux';
import { setCreationFlow } from 'src/store/slices/creationFlowSlices';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { booleanScheme, stringEmailScheme, stringScheme } from 'src/utils/validation-schemes';
import { CreateProjectHead } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectHead/CreateProjectHead';
import { Skeleton } from 'src/components/ui/skeleton';
import * as yup from 'yup';
import { validateWorkflowActions } from 'src/helpers/validateWorkflowActions';
import { getWorkflowRequestAction } from 'src/pages/ConfigurationPage/WorkflowsTab/getWorkflowRequestAction';
import { EscalatedList } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep6/EscalatedList/EscalatedList';
import { API_VERSION } from 'src/constants/api-routes';

const LoadingSkeleton = () => {
  return (
    <div className={'w-[455px] flex flex-col gap-5'}>
      <Skeleton className={'w-full h-4 mb-10'} />
      <Skeleton className={'w-full h-20'} />
      <Skeleton className={'w-full h-96'} />
    </div>
  );
};

const CreateProjectStep6Page = () => {
  const [settingsPage, setSettingsPage] = useState(1);
  const [settings, setSettings] = useState(null);
  const [activeSettings, setActiveSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [inputData, setInputData] = useState(null);
  const [workflow, setWorkflow] = useState(inputData?.pf_default_workflow || null);
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const { id, step } = useParams();
  const loader = useLoader();
  const navigate = useNavigate();
  const { projectType } = useAppSelector((s) => s.projectsSlices);
  const projectTypeVar = projectType || type;
  const creationFlowState = useAppSelector((s) => s.creationFlowSlices);
  const dispatch = useDispatch();
  const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;
  const isV2 = version === API_VERSION.V2;
  const { data: allConfigurationSettings, isLoading: isLoadingAllConfigurationSettings } =
    useGetAllConfigurationSettingsQuery(
      {
        id: id,
        page: settingsPage,
      },
      { skip: !Boolean(id), refetchOnMountOrArgChange: true },
    );
  const [
    updateConfigurationSettings,
    { data: updateConfigurationSettingsData, isLoading: isLoadingUpdateConfigurationSettings },
  ] = useUpdateConfigurationSettingsPromptMutation();
  const { data: configurationSettingsPrompt, isLoading: isLoadingConfigurationSettingsPrompt } =
    useGetConfigurationSettingsPromptQuery(
      { project_id: id, settings_id: activeSettings && activeSettings.id },
      {
        skip: !Boolean(id && activeSettings),
        refetchOnMountOrArgChange: true,
      },
    );

  useEffect(() => {
    if (
      isLoadingAllConfigurationSettings ||
      isLoadingConfigurationSettingsPrompt ||
      isLoadingUpdateConfigurationSettings ||
      settings === null
    ) {
      setIsLoading(true);
      loader.show();
    } else {
      setIsLoading(false);
      loader.hide();
    }
  }, [
    isLoadingAllConfigurationSettings,
    isLoadingConfigurationSettingsPrompt,
    isLoadingUpdateConfigurationSettings,
    settings,
  ]);

  useEffect(() => {
    if (settings) {
      const config = creationFlowState[id];
      const settingsId = config.promptBuilderSettingsId;

      if (settingsId && settings.length) {
        const setting = settings.find((setting) => `${setting.id}` === `${settingsId}`);

        setting && setActiveSettings(setting);
      }
    }
  }, [settings, creationFlowState]);

  useEffect(() => {
    if (configurationSettingsPrompt) {
      setInputData((configurationSettingsPrompt as any).input_data);
    }
  }, [configurationSettingsPrompt]);

  useEffect(() => {
    if (allConfigurationSettings) {
      setSettings((prevState) => {
        const prev = prevState || [];
        const newState = [...prev];

        (allConfigurationSettings as any).items.forEach((item) => {
          const index = prev.findIndex((prevItem) => {
            return prevItem.id === item.id;
          });

          if (index > -1) {
            newState[index] = item;
          } else {
            newState.push(item);
          }
        });

        newState.sort((a, b) => +moment(b.created_at) - +moment(a.created_at));

        return newState;
      });

      if ((allConfigurationSettings as any).page_info.has_next_page) {
        setSettingsPage((allConfigurationSettings as any).page_info.page + 1);
      }
    }
  }, [allConfigurationSettings]);

  useEffect(() => {
    if (inputData) {
      const workflow = inputData?.pf_default_workflow;
      const action = [workflow.action];

      methods.reset({
        name: workflow?.name || 'Escalation',
        description: workflow?.description || 'When AI lacks the knowledge to assist.',
        trigger_type: workflow?.trigger_type || 'when_asked',
        trigger_value: workflow?.trigger_value || '',
        action: action.map((action) => {
          const customActionFields: Record<string, any> = {};
          if (
            action.fields &&
            Boolean(action.fields.filter((field) => field.type === 'custom').length)
          ) {
            customActionFields.action_fields_custom_array = action.fields.filter(
              (field) => field.type === 'custom',
            );
          }

          return {
            action_type:
              action.type &&
              ['collect_and_escalate', 'forward_to_email', 'handle_with_crm', 'close'].includes(
                action.type,
              )
                ? 'escalate'
                : action.type || 'clarify',
            action_fields_custom: Boolean(
              action.fields && action.fields.filter((field) => field.type === 'custom').length,
            ),
            action_fields_name: Boolean(
              action.fields && action.fields.filter((field) => field.type === 'name').length,
            ),
            action_fields_email: Boolean(
              action.fields && action.fields.filter((field) => field.type === 'email').length,
            ),
            action_fields_phone_number: Boolean(
              action.fields &&
              action.fields.filter((field) => field.type === 'phone_number').length,
            ),
            action_message_type: action.message_type || 'transcript',
            message_before: action.message_before || '',
            receiver_email_addresses: action.receiver_email_addresses
              ? action.receiver_email_addresses.join(', ')
              : '',
            sender_name: action.sender_name,
            subject: action.subject,
            sender_email_address: action.sender_email_address,
            message_after: action.message_after || '',
            action_message: action.message || '',
            action_escalated_list: action.type || '',
            action_data_collection: '',
            message_close: action.message || '',
            ...customActionFields,
          };
        }),
      });

      setWorkflow(workflow);
    }
  }, [inputData]);

  useEffect(() => {
    const config = creationFlowState[id];

    if (config && +config.step < +step) {
      dispatch(
        setCreationFlow({
          id: id,
          step: step,
        }),
      );
    }
  }, []);

  useEffect(() => {
    if (updateConfigurationSettingsData) {
      next();
    }
  }, [updateConfigurationSettingsData]);

  const methods = useForm<any>({
    defaultValues: async () => {
      const action = workflow ? [workflow.action] : [];

      return {
        name: workflow?.name || 'Escalation',
        description: workflow?.description || 'When AI lacks the knowledge to assist.',
        trigger_type: workflow?.trigger_type || 'when_asked',
        trigger_value: workflow?.trigger_value || '',
        action: action.map((action) => {
          const customActionFields: Record<string, any> = {};
          if (
            action.fields &&
            Boolean(action.fields.filter((field) => field.type === 'custom').length)
          ) {
            customActionFields.action_fields_custom_array = action.fields.filter(
              (field) => field.type === 'custom',
            );
          }

          return {
            action_type:
              action.type &&
              ['collect_and_escalate', 'forward_to_email', 'handle_with_crm', 'close'].includes(
                action.type,
              )
                ? 'escalate'
                : action.type || 'clarify',
            action_fields_custom: Boolean(
              action.fields && action.fields.filter((field) => field.type === 'custom').length,
            ),
            action_fields_name: Boolean(
              action.fields && action.fields.filter((field) => field.type === 'name').length,
            ),
            action_fields_email: Boolean(
              action.fields && action.fields.filter((field) => field.type === 'email').length,
            ),
            action_fields_phone_number: Boolean(
              action.fields &&
              action.fields.filter((field) => field.type === 'phone_number').length,
            ),
            action_message_type: action.message_type || '',
            message_before: action.message_before || '',
            receiver_email_addresses: action.receiver_email_addresses
              ? action.receiver_email_addresses.join(', ')
              : '',
            sender_name: action.sender_name,
            subject: action.subject,
            sender_email_address: action.sender_email_address,
            message_after: action.message_after || '',
            action_message: action.message || '',
            action_escalated_list: action.type || '',
            action_data_collection: '',
            message_close: action.message || '',
            ...customActionFields,
          };
        }),
      };
    },
    resolver: yupResolver(
      yup.object({
        name: stringScheme({ required: true }),
        description: stringScheme({ required: true }),
        trigger_type: stringScheme({ required: false }),
        trigger_value: stringScheme({ required: false }),
        action: yup.array(
          yup.object({
            action_type: stringScheme({ required: false }),
            action_message: stringScheme({ required: false }),
            action_escalated_list: stringScheme({ required: false }),
            action_data_collection: stringScheme({ required: false }),
            action_fields_name: booleanScheme({ required: false }),
            action_fields_email: booleanScheme({ required: false }),
            action_fields_phone_number: booleanScheme({ required: false }),
            action_fields_custom: booleanScheme({ required: false }),
            action_message_type: stringScheme({ required: false }),
            message_before: stringScheme({ required: false }),
            receiver_email_addresses: stringEmailScheme({ required: false, format: true }),
            message_after: stringScheme({ required: false }),
            message_close: stringScheme({ required: false }),
            action_fields_custom_array: yup.array(
              yup.object({
                name: stringScheme({ required: false }),
                description: stringScheme({ required: false }),
              }),
            ),
          }),
        ),
      }),
    ),
  });

  const submit = (data) => {
    const is = {
      valid: validateWorkflowActions(data, methods.setError),
      dirty: Boolean(Object.keys(methods.formState.dirtyFields).length),
      deployed: activeSettings && activeSettings.is_deployed,
    };

    if (!is.valid) {
      return;
    }

    if (activeSettings) {
      const action = data.action.map((action) => getWorkflowRequestAction(action));
      const newWorkflow = {
        enabled: workflow.enabled,
        type: workflow.type,
        action: action[0],
      };

      const body = {
        input_data: {
          ...inputData,
          pf_default_workflow: newWorkflow,
        },
      };

      updateConfigurationSettings({ project_id: id, settings_id: activeSettings.id, body });
    }
  };

  const next = () => {
    navigate(
      projectTypeVar === ProductsType.customer
        ? `${PATH_DASHBOARD.PROJECTS}/${id}/new/${+step + 1}?type=${projectTypeVar}`
        : `${PATH_DASHBOARD.PROJECTS}/${id}?type=${projectTypeVar}`,
      { replace: true },
    );
  };

  const back = () => {
    navigate(
      projectTypeVar === ProductsType.customer
        ? `${PATH_DASHBOARD.PROJECTS}/${id}/new/${+step - 1}?type=${projectTypeVar}`
        : `${PATH_DASHBOARD.PROJECTS}/${id}?type=${projectTypeVar}`,
      { replace: true },
    );
  };

  return (
    <React.Fragment>
      <PageContentWrapper
        menuBarVisible={false}
        className={'create-project-step items-center !gap-5'}
      >
        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <CreateProjectStepsTitle
              step={isV2 ? +step - 1 : +step}
              title={'Escalation Workflow'}
            />
            <CreateProjectHead step={+step} titleIndex={6} />
            <div className={'flex flex-col items-center w-[455px] gap-5'}>
              <FormProvider {...methods}>
                <form
                  id={'create-project-step-6'}
                  className={'flex flex-col gap-5 w-full min-w-[455px] max-w-[455px]'}
                  onSubmit={methods.handleSubmit(submit)}
                >
                  <EscalatedList
                    control={methods.control}
                    watch={methods.watch}
                    resetField={methods.resetField}
                    listItemContentClassName={'!bg-white'}
                  />
                </form>
              </FormProvider>
              <Button
                form="create-project-step-6"
                type="submit"
                className="min-w-24 w-full flex flex-row h-12 mt-5"
              >
                Continue
              </Button>
              <div
                onClick={back}
                className={'text-[#3F60B3] text-xs font-medium cursor-pointer hover:underline'}
              >
                ← Back
              </div>
            </div>
          </>
        )}
      </PageContentWrapper>
    </React.Fragment>
  );
};

export default CreateProjectStep6Page;
